<script setup lang="ts">
import { onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { RouterLink, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useCartStore } from '@/stores/cart'
import { useNavMenuStore } from '@/stores/navMenu'
import ContentEditModePill from '@/components/ContentEditModePill.vue'

// The uppercase Broadsheet subnav under the masthead. Items come from the navMenu store (coded
// default until an admin saves a menu); the trailing Cart / Sign in / account entries are
// auth-aware and stay coded. The cart count badge reads the cart store, which seeds from /me's
// cartCount and switches to live cart state after any cart mutation. The admin edit-mode pill
// rides along (it's position:fixed and self-hides).
const auth = useAuthStore()
const { isAuthenticated, isAdmin } = storeToRefs(auth)
const cartStore = useCartStore()
const navMenu = useNavMenuStore()
const router = useRouter()

onMounted(() => navMenu.load())

async function signOut() {
  try {
    await auth.logout()
    cartStore.reset()
  } finally {
    router.push('/')
  }
}
</script>

<template>
  <nav class="bs-subnav" aria-label="Primary">
    <template v-for="item in navMenu.items" :key="item.label">
      <a v-if="item.external" :href="item.href" target="_blank" rel="noopener">{{ item.label }}</a>
      <RouterLink v-else :to="item.href">{{ item.label }}</RouterLink>
    </template>

    <RouterLink v-if="!isAuthenticated" class="bs-signin" to="/login">Sign in</RouterLink>
    <template v-else>
      <RouterLink class="bs-signin" to="/cart" data-test="cart-link">
        Cart<span v-if="cartStore.count > 0" class="bs-cart-count">({{ cartStore.count }})</span>
      </RouterLink>
      <RouterLink class="bs-signin" to="/account">My Account</RouterLink>
      <RouterLink v-if="isAdmin" class="bs-signin" to="/admin">Admin</RouterLink>
      <a href="#" class="bs-signin" @click.prevent="signOut">Sign out</a>
    </template>
  </nav>

  <!-- Admin-only floating toggle for in-place content editing (self-hides for non-admins). -->
  <ContentEditModePill />
</template>

<style scoped>
.bs-cart-count {
  margin-left: 4px;
  color: var(--accent, #1f6fd4);
  font-weight: 700;
}
</style>
